import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@helpers/translate";
import { useSelector } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";
import { symbolsMatch, normalizeOrderSide } from "@helpers/formatOrders";
import { formatPrice } from "@helpers/formatPrice";
import { Share } from "./Share";
import { TradeCell } from "./TradeCell";
import { CustomScrollTable } from "../CustomScrollTable";
import { OrderCardItem } from "./OrderCardItem";
import { isNestedControlClick } from "./rowClick";
import { OrdersEmptyState } from "./OrdersEmptyState";
import { useOrdersLayout } from "./OrdersLayoutContext";
import {
  TradeDetailsModal,
  fromPosition,
  isDemoWallet,
} from "../TradeModals";
import "./index.sass";
import { usePositionHistoryPages } from "./usePositionHistoryPages";
import { useLoadMoreOnReveal } from "./useLoadMoreOnReveal";
import "./Positions.sass";
import "./PositionHistory.sass";

const toNumber = (value) => {
  if (value == null || value === "") return null;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : null;
};

const formatWager = (order) => {
  const amount = toNumber(order?.margin);
  if (amount == null) return "—";
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDollarPrice = (value) => {
  const numeric = toNumber(value);
  if (numeric == null) return "—";
  return `$${formatPrice(numeric)}`;
};

const formatSignedPnl = (value) => {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (value > 0) return `+$${abs}`;
  if (value < 0) return `-$${abs}`;
  return `$${abs}`;
};

const formatTpSlAmount = (value) => {
  if (value == null || !Number.isFinite(value)) return null;
  const rounded = Math.round(Math.abs(value) * 100) / 100;
  const text = rounded.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  if (value > 0) return `+${text}$`;
  if (value < 0) return `-${text}$`;
  return `${text}$`;
};

const CLOSED_TRADES_PAGE_SIZE = 20;

const getTriggerPnl = (triggerPrice, entryPrice, quantity, isSellSide) => {
  const target = toNumber(triggerPrice);
  if (target == null || !entryPrice || !quantity) return null;
  return isSellSide
    ? (entryPrice - target) * quantity
    : (target - entryPrice) * quantity;
};

export const PositionHistory = ({
  showAllMarkets,
  data,
  setData,
  forceUpdate = 0,
  onTotalChange,
}) => {
  const [shareActive, setShareActive] = useState(false);
  // Closed trade on the P&L share card, same one the trade details share
  const [shareOrder, setShareOrder] = useState(null);
  const [tradeDetails, setTradeDetails] = useState(null);
  const { __ } = useTranslation();
  const { pair } = useSelector((state) => state.futures);
  const {
    wallet: { active_wallet },
  } = useSelector((state) => state);
  const activeWalletId = active_wallet?.id;
  const { loading, canLoadMore, loadMore } = usePositionHistoryPages({
    walletId: activeWalletId,
    reloadKey: forceUpdate,
    perPage: CLOSED_TRADES_PAGE_SIZE,
    setData,
    onTotalChange,
  });
  const sentinelRef = useLoadMoreOnReveal({
    enabled: canLoadMore && !loading,
    onReveal: loadMore,
  });
  const isMobileQuery = useMediaQuery("(max-width: 768px)");
  const { isCompactCards } = useOrdersLayout();
  const isMobile = isMobileQuery || isCompactCards;

  const isFinitePositive = (value) =>
    Number.isFinite(Number(value)) && Number(value) > 0;

  const isRenderableHistoryPosition = (item) => {
    if (!item?.id) return false;
    if (
      !item?.wallet_id ||
      !activeWalletId ||
      item.wallet_id !== activeWalletId
    ) {
      return false;
    }
    if (!item?.symbol || !item?.side || !item?.created_at) return false;
    return (
      isFinitePositive(item?.quantity) ||
      isFinitePositive(String(item?.size ?? "0").replace(/,/g, "")) ||
      isFinitePositive(item?.margin)
    );
  };

  const filteredData = useMemo(() => {
    const walletSafeData = data.filter(isRenderableHistoryPosition);
    return showAllMarkets
      ? walletSafeData
      : walletSafeData.filter((order) => symbolsMatch(order.symbol, pair.slug));
  }, [data, pair, showAllMarkets, activeWalletId]);

  // A filter can hide a whole page: keep fetching until something matches or the pages run out
  useEffect(() => {
    if (!loading && canLoadMore && !filteredData.length) loadMore();
  }, [loading, canLoadMore, filteredData.length, loadMore]);

  const openTradeDetails = (order) =>
    setTradeDetails(
      fromPosition(order, {
        placedBy: __("futures.trade_details.me"),
        isDemo: isDemoWallet(active_wallet),
      }),
    );

  // Buttons in the row keep their own actions
  const handleRowClick = (event, order) => {
    if (isNestedControlClick(event)) return;

    openTradeDetails(order);
  };

  if (isMobile && !loading && !canLoadMore && !filteredData.length) {
    return <OrdersEmptyState className="orders-empty-state--fill" />;
  }

  return (
    <div className="position-history-table">
      {isMobile ? (
        <div className="position-item-table__mobile">
          <CustomScrollTable
            isTable={false}
            maxHeight={392}
            className="order-card-list"
          >
            {filteredData.map((order, index) => (
              <OrderCardItem
                key={order.id ?? index}
                order={order}
                isHistory={true}
                onCardClick={() => openTradeDetails(order)}
              />
            ))}
            {canLoadMore && (
              <div ref={sentinelRef} className="order-card-list__sentinel" />
            )}
          </CustomScrollTable>
        </div>
      ) : (
          <div className="closed-trades-table">
          <CustomScrollTable
            className="futures-orders-table futures-positions-table futures-position-history-table futures-closed-trades"
            scrollable={loading || filteredData.length > 0}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className="closed-trades-table__head closed-trades-table__head--trade">
                    {__("futures.community.trade")}
                  </TableCell>
                  <TableCell className="closed-trades-table__head">
                    {__("futures.community.wager")}
                  </TableCell>
                  <TableCell className="closed-trades-table__head">
                    {__("futures.community.leverage")}
                  </TableCell>
                  <TableCell className="closed-trades-table__head">
                    {__("futures.community.entry")}
                  </TableCell>
                  <TableCell className="closed-trades-table__head">
                    {__("futures.community.exit")}
                  </TableCell>
                  <TableCell className="closed-trades-table__head">
                    {__("futures.community.profit_loss")}
                  </TableCell>
                  <TableCell className="closed-trades-table__head">
                    {__("futures.tp_sl.column")}
                  </TableCell>
                  <TableCell className="closed-trades-table__head closed-trades-table__head--action">
                    {__("futures.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !filteredData.length
                  ? Array.from({ length: 3 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell colSpan={8}>
                          <Skeleton
                            className="main-skeleton"
                            variant="rounded"
                            height={20}
                            width="100%"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  : !filteredData.length
                    ? (
                      <TableRow className="closed-trades-table__empty-row">
                        <TableCell className="closed-trades-table__empty" colSpan={8}>
                          <OrdersEmptyState className="orders-empty-state--in-table" />
                        </TableCell>
                      </TableRow>
                    )
                  : filteredData.map((order, index) => {
                      const { isSellSide } = normalizeOrderSide(order?.side);
                      const entryPrice =
                        toNumber(
                          order?.entry_price ??
                            order?.fill_price ??
                            order?.price,
                        ) ?? 0;
                      const quantity = Math.abs(
                        toNumber(order?.quantity ?? order?.qty) ?? 0,
                      );
                      const leverage = toNumber(order?.leverage);
                      const pnlValue = toNumber(order?.pnl);
                      const pnlClass =
                        pnlValue == null || pnlValue === 0
                          ? ""
                          : pnlValue > 0
                            ? "closed-trades-table__pnl--positive"
                            : "closed-trades-table__pnl--negative";
                      const takeProfit = formatTpSlAmount(
                        getTriggerPnl(
                          order?.take_profit_trigger_price,
                          entryPrice,
                          quantity,
                          isSellSide,
                        ),
                      );
                      const stopLoss = formatTpSlAmount(
                        getTriggerPnl(
                          order?.stop_loss_trigger_price,
                          entryPrice,
                          quantity,
                          isSellSide,
                        ),
                      );

                      return (
                        <TableRow
                          key={order.id ?? index}
                          className="trade-table__row--clickable"
                          onClick={(event) => handleRowClick(event, order)}
                        >
                          <TableCell className="closed-trades-table__cell closed-trades-table__cell--trade">
                            <TradeCell
                              symbol={order.symbol}
                              isLong={!isSellSide}
                              iconSize={16}
                              longColor="#2FA766"
                              shortColor="#E2464A"
                            />
                          </TableCell>
                          <TableCell className="closed-trades-table__cell">
                            {formatWager(order)}
                          </TableCell>
                          <TableCell className="closed-trades-table__cell">
                            {leverage != null ? `${leverage}x` : "—"}
                          </TableCell>
                          <TableCell className="closed-trades-table__cell">
                            {formatDollarPrice(
                              order?.entry_price ??
                                order?.fill_price ??
                                order?.price,
                            )}
                          </TableCell>
                          <TableCell className="closed-trades-table__cell">
                            {formatDollarPrice(
                              order?.closed_price ?? order?.close_price,
                            )}
                          </TableCell>
                          <TableCell
                            className={`closed-trades-table__cell closed-trades-table__cell--pnl ${pnlClass}`}
                          >
                            {formatSignedPnl(pnlValue)}
                          </TableCell>
                          <TableCell className="closed-trades-table__cell closed-trades-table__cell--tpsl">
                            {takeProfit || stopLoss ? (
                              <div className="closed-trades-table__tpsl">
                                <span className="closed-trades-table__tpsl-side closed-trades-table__tpsl-side--tp">
                                  {takeProfit && (
                                    <span className="closed-trades-table__tp">
                                      {takeProfit}
                                    </span>
                                  )}
                                </span>
                                <span className="closed-trades-table__tpsl-divider" />
                                <span className="closed-trades-table__tpsl-side closed-trades-table__tpsl-side--sl">
                                  {stopLoss && (
                                    <span className="closed-trades-table__sl">
                                      {stopLoss}
                                    </span>
                                  )}
                                </span>
                              </div>
                            ) : (
                              <span className="closed-trades-table__tpsl-empty">
                                —
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="closed-trades-table__cell closed-trades-table__cell--action">
                            <button
                              type="button"
                              className="closed-trades-table__share-btn"
                              aria-label={__("futures.share")}
                              onClick={() => {
                                setShareOrder(order);
                                setShareActive(true);
                              }}
                            >
                              {__("futures.trade_details.share")}
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                {canLoadMore && (
                  <TableRow className="closed-trades-table__sentinel-row">
                    <TableCell colSpan={8} ref={sentinelRef} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CustomScrollTable>
        </div>
      )}

      <Share
        active={shareActive}
        setActive={setShareActive}
        order={shareOrder ?? {}}
        pnl={toNumber(shareOrder?.realized_pnl ?? shareOrder?.pnl)?.toFixed(2) ?? "0"}
        markPrice={shareOrder?.closed_price ?? shareOrder?.close_price ?? null}
      />

      <TradeDetailsModal
        trade={tradeDetails}
        onClose={() => setTradeDetails(null)}
      />
    </div>
  );
};
